import React, { useState } from 'react'
import {
  ZoomIn, ZoomOut, Maximize2, Minimize2,
  Sparkles, ChevronDown,
} from 'lucide-react'
import { RESOURCE_TYPES } from '../../constants/resourceTypes'
import type { LayoutType } from './ConnectionsGraph'
import type { ResourceType } from '../../types'

interface ConnectionsControlsProps {
  layout: LayoutType
  onLayoutChange: (layout: LayoutType) => void
  onZoomIn: () => void
  onZoomOut: () => void
  onFullscreen: () => void
  isFullscreen: boolean
  onDiscover: (resourceTypes?: ResourceType[]) => void
  isDiscovering: boolean
  nodeCount: number
  edgeCount: number
  hasSelectedNode: boolean
}

const LAYOUT_OPTIONS: { id: LayoutType; label: string }[] = [
  { id: 'cose', label: 'Force-Directed' },
  { id: 'circle', label: 'Circle' },
  { id: 'grid', label: 'Grid' },
  { id: 'breadthfirst', label: 'Hierarchy' },
]

const ConnectionsControls: React.FC<ConnectionsControlsProps> = ({
  layout,
  onLayoutChange,
  onZoomIn,
  onZoomOut,
  onFullscreen,
  isFullscreen,
  onDiscover,
  isDiscovering,
  nodeCount,
  edgeCount,
  hasSelectedNode,
}) => {
  const [showFilters, setShowFilters] = useState(false)
  const [selectedTypes, setSelectedTypes] = useState<Set<ResourceType>>(
    new Set(RESOURCE_TYPES.map(r => r.id as ResourceType))
  )

  const toggleResourceType = (type: ResourceType) => {
    setSelectedTypes(prev => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type)
      else next.add(type)
      return next
    })
  }

  const handleDiscover = () => {
    const types = selectedTypes.size === RESOURCE_TYPES.length
      ? undefined
      : Array.from(selectedTypes)
    onDiscover(types)
  }

  return (
    <div className="absolute top-3 left-3 z-20 flex flex-col gap-2">
      {/* Layout selector and zoom */}
      <div className="bg-white/95 backdrop-blur rounded-xl border border-gray-200 shadow-sm p-2">
        {/* Layout dropdown */}
        <div className="mb-2">
          <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider block mb-1 px-1">
            Layout
          </label>
          <select
            value={layout}
            onChange={(e) => onLayoutChange(e.target.value as LayoutType)}
            className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white
                       focus:outline-none focus:ring-1 focus:ring-acad-500"
          >
            {LAYOUT_OPTIONS.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Zoom controls */}
        <div className="flex items-center gap-1">
          <button
            onClick={onZoomIn}
            className="w-7 h-7 flex items-center justify-center bg-gray-50 border border-gray-200
                       rounded-lg hover:bg-acad-50 hover:border-acad-300 transition-colors"
            title="Zoom in"
          >
            <ZoomIn className="w-3.5 h-3.5 text-gray-600" />
          </button>
          <button
            onClick={onZoomOut}
            className="w-7 h-7 flex items-center justify-center bg-gray-50 border border-gray-200
                       rounded-lg hover:bg-acad-50 hover:border-acad-300 transition-colors"
            title="Zoom out"
          >
            <ZoomOut className="w-3.5 h-3.5 text-gray-600" />
          </button>
          <button
            onClick={onFullscreen}
            className="w-7 h-7 flex items-center justify-center bg-gray-50 border border-gray-200
                       rounded-lg hover:bg-acad-50 hover:border-acad-300 transition-colors"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? (
              <Minimize2 className="w-3.5 h-3.5 text-gray-600" />
            ) : (
              <Maximize2 className="w-3.5 h-3.5 text-gray-600" />
            )}
          </button>
        </div>

        {/* Node/edge count */}
        <div className="mt-2 pt-2 border-t border-gray-100 px-1">
          <p className="text-[10px] text-gray-400">
            {nodeCount} node{nodeCount !== 1 ? 's' : ''}, {edgeCount} edge{edgeCount !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Discover button + type filter */}
      <div className="bg-white/95 backdrop-blur rounded-xl border border-gray-200 shadow-sm p-2">
        <button
          onClick={handleDiscover}
          disabled={isDiscovering || !hasSelectedNode}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold
                     text-white bg-acad-500 rounded-lg hover:bg-acad-600 transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed"
          title={hasSelectedNode ? 'Discover 25 more related resources' : 'Select a node first'}
        >
          <Sparkles className="w-3.5 h-3.5" />
          Discover 25 More
        </button>

        {/* Resource type filter toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full flex items-center justify-between mt-1.5 px-2 py-1 text-[10px]
                     text-gray-500 hover:text-acad-600 transition-colors"
        >
          <span>Filter resource types</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </button>

        {showFilters && (
          <div className="mt-1 space-y-0.5 px-1">
            {RESOURCE_TYPES.map(rt => (
              <label key={rt.id} className="flex items-center gap-2 py-0.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedTypes.has(rt.id as ResourceType)}
                  onChange={() => toggleResourceType(rt.id as ResourceType)}
                  className="w-3 h-3 rounded border-gray-300 text-acad-500
                             focus:ring-acad-500 focus:ring-1"
                />
                <span className="text-[10px] text-gray-600">{rt.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default ConnectionsControls
